import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MessageService } from 'primeng/api';
import { EstadoService } from '../../../services/configuracoes/estado.service';
import { EstadoDTO } from '../../../models/configuracoes/estado.model';
import { CidadeDTO, CidadeFormDTO } from '../../../models/configuracoes/cidade.model';
import { CidadeService } from '../../../services/configuracoes/cidade.service';
import { TranslateService } from '@ngx-translate/core';
import { TableColumn } from '../../../models/shared/table-column.model';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-configuracao-cidade',
  templateUrl: './configuracao-cidade.component.html',
  styleUrls: ['./configuracao-cidade.component.scss'],
  providers: [MessageService],
})
export class ConfiguracaoCidadeComponent implements OnInit, OnDestroy {
  // Controle de abas (0 = Cadastro, 1 = Lista)
  activeTabIndex: number = 1;

  // Controle de validacao visual
  isFormSubmitted = false;
  deleteDialogVisible = false;
  deleteItemId: number | null = null;

  // Objetos de traducao
  texts: any = {};
  labels: any = {};
  placeholders: any = {};
  botoes: any = {};
  tooltips: any = {};

  private langChangeSub!: Subscription;

  estadosList: { label: string; value: number }[] = [];
  estados: EstadoDTO[] = [];
  cidades: CidadeDTO[] = [];
  cidadesOriginal: CidadeDTO[] = [];
  form: CidadeFormDTO = this.createEmptyForm();
  filterName = '';
  loading = true;
  first = 0;
  rows = 10;
  totalRecords = 0;
  lastfield = 'id';
  lastSort = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'configuracao-cidade-visible-columns';

  columns: TableColumn[] = [
    { key: 'id', labelKey: 'configuracao-cidade.labels.id', visible: true, fixed: true, defaultVisible: true },
    { key: 'nome', labelKey: 'configuracao-cidade.labels.nome', visible: true, fixed: false, defaultVisible: true },
    { key: 'estado', labelKey: 'configuracao-cidade.labels.estado', visible: true, fixed: false, defaultVisible: true },
    { key: 'codigoDistritoFaturaEletronica', labelKey: 'configuracao-cidade.labels.codigo_distrito_fatura_eletronica', visible: true, fixed: false, defaultVisible: true },
    { key: 'codigoLocalidadeFaturaEletronica', labelKey: 'configuracao-cidade.labels.codigo_localidade_fatura_eletronica', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'configuracao-cidade.labels.data_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'configuracao-cidade.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  private search$ = new Subject<string>();

  constructor(
    private messageService: MessageService,
    private estadoService: EstadoService,
    private cidadeService: CidadeService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    await this.findEstado();
    this.estadosList = this.getEstados();
    this.findCidade();
    this.setupSearchDebounce();
    this.loadTranslations();
    this.loadColumnPreferences();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.titulo = this.translate.instant('configuracao-cidade.texts.titulo');
      this.texts.subtitulo = this.translate.instant('configuracao-cidade.texts.subtitulo_lista');
    } else {
      const isEdicao = !!this.form.id;
      this.texts.titulo = this.translate.instant(
        isEdicao ? 'configuracao-cidade.texts.titulo_editar' : 'configuracao-cidade.texts.titulo'
      );
      this.texts.subtitulo = this.translate.instant(
        isEdicao ? 'configuracao-cidade.texts.subtitulo_editar' : 'configuracao-cidade.texts.subtitulo_nova'
      );
    }
  }

  private loadTranslations(): void {
    const isEdicao = !!this.form.id;
    const isListaAtiva = this.activeTabIndex === 1;
    this.texts = {
      titulo: this.translate.instant(
        isListaAtiva ? 'configuracao-cidade.texts.titulo' : isEdicao ? 'configuracao-cidade.texts.titulo_editar' : 'configuracao-cidade.texts.titulo'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva
          ? 'configuracao-cidade.texts.subtitulo_lista'
          : isEdicao ? 'configuracao-cidade.texts.subtitulo_editar' : 'configuracao-cidade.texts.subtitulo_nova'
      ),
      aba_cadastro: this.translate.instant('configuracao-cidade.texts.aba_cadastro'),
      aba_lista: this.translate.instant('configuracao-cidade.texts.aba_lista'),
      dados_da_cidade: this.translate.instant('configuracao-cidade.texts.dados_da_cidade'),
      editando_cidade: this.translate.instant('configuracao-cidade.texts.titulo_editar'),
      nova_cidade: this.translate.instant('configuracao-cidade.texts.nova_cidade'),
      tabela_de_cidades: this.translate.instant('configuracao-cidade.texts.tabela_de_cidades'),
      nenhuma_cidade: this.translate.instant('configuracao-cidade.texts.nenhuma_cidade'),
      ajuste_filtros: this.translate.instant('configuracao-cidade.texts.ajuste_filtros'),
      carregando: this.translate.instant('configuracao-cidade.texts.carregando'),
      confirmacao: this.translate.instant('common.confirmacao'),
      confirmar_exclusao: this.translate.instant('Certeza_que_deseja_excluir_esta_cidade?'),
    };

    this.labels = {
      id: this.translate.instant('configuracao-cidade.labels.id'),
      nome: this.translate.instant('configuracao-cidade.labels.nome'),
      estado: this.translate.instant('configuracao-cidade.labels.estado'),
      nome_cidade_nota_eletronica: this.translate.instant('configuracao-cidade.labels.nome_cidade_nota_eletronica'),
      nome_localidade_nota_eletronica: this.translate.instant('configuracao-cidade.labels.nome_localidade_nota_eletronica'),
      codigo_distrito_fatura_eletronica: this.translate.instant('configuracao-cidade.labels.codigo_distrito_fatura_eletronica'),
      codigo_localidade_fatura_eletronica: this.translate.instant('configuracao-cidade.labels.codigo_localidade_fatura_eletronica'),
      data_inclusao: this.translate.instant('configuracao-cidade.labels.data_inclusao'),
      acoes: this.translate.instant('configuracao-cidade.labels.acoes'),
      itens_por_pagina: this.translate.instant('configuracao-cidade.labels.itens_por_pagina'),
    };

    this.placeholders = {
      nome: this.translate.instant('configuracao-cidade.placeholders.nome'),
      selecione: this.translate.instant('configuracao-cidade.placeholders.selecione'),
      nome_cidade_nota_eletronica: this.translate.instant('configuracao-cidade.placeholders.nome_cidade_nota_eletronica'),
      nome_localidade_nota_eletronica: this.translate.instant('configuracao-cidade.placeholders.nome_localidade_nota_eletronica'),
      codigo_distrito_fatura_eletronica: this.translate.instant('configuracao-cidade.placeholders.codigo_distrito_fatura_eletronica'),
      codigo_localidade_fatura_eletronica: this.translate.instant('configuracao-cidade.placeholders.codigo_localidade_fatura_eletronica'),
      pesquisar: this.translate.instant('configuracao-cidade.placeholders.pesquisar'),
    };

    this.botoes = {
      salvar: this.translate.instant('configuracao-cidade.botoes.salvar'),
      atualizar: this.translate.instant('configuracao-cidade.botoes.atualizar'),
      limpar: this.translate.instant('configuracao-cidade.botoes.limpar'),
      cancelar: this.translate.instant('common.cancelar'),
      confirmar: this.translate.instant('common.confirmar'),
    };

    this.tooltips = {
      editar: this.translate.instant('configuracao-cidade.tooltips.editar'),
      excluir: this.translate.instant('configuracao-cidade.tooltips.excluir'),
    };
  }

  // =========================================================================
  // Navegacao entre abas
  // =========================================================================

  novaCidade(): void {
    this.limparForm();
    this.activeTabIndex = 0;
  }

  editarCidade(event: CidadeDTO): void {
    const estado = this.estadosList.find(
      (p) => Number(p.value) === Number(event.estado_id)
    );

    this.form = {
      id: event.id,
      nome: event.nome,
      estado: estado ? { label: estado.label, value: Number(estado.value) } : undefined,
      codigoDistritoFaturaEletronica: event.codigoDistritoFaturaEletronica,
      codigoLocalidadeFaturaEletronica: event.codigoLocalidadeFaturaEletronica,
      nomeCidadeNotaEletronica: event.nomeCidadeNotaEletronica,
      nomeLocalidadeNotaEletronica: event.nomeLocalidadeNotaEletronica,
    };

    this.activeTabIndex = 0;
    this.texts.titulo = this.translate.instant('configuracao-cidade.texts.titulo_editar');
    this.texts.subtitulo = this.translate.instant('configuracao-cidade.texts.subtitulo_editar');
    this.cdr.detectChanges();
  }

  limparForm(): void {
    this.isFormSubmitted = false;
    this.form = this.createEmptyForm();
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }

  // =========================================================================
  // Busca
  // =========================================================================

  private setupSearchDebounce() {
    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.first = 0;
      if (term && term.trim().length > 0) {
        this.filtrarCidadesLocal(term.trim());
      } else {
        this.cidades = [...this.cidadesOriginal];
        this.totalRecords = this.cidadesOriginal.length;
      }
    });
  }

  onSearchChange(value: string) {
    this.filterName = value || '';
    this.search$.next(this.filterName);
  }

  private filtrarCidadesLocal(term: string): void {
    const termLower = term.toLowerCase();

    this.cidades = this.cidadesOriginal.filter(cidade => {
      if (this.isColumnVisible('id') && cidade.id != null) {
        if (String(cidade.id).includes(termLower)) return true;
      }
      if (this.isColumnVisible('nome') && cidade.nome) {
        if (cidade.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('estado') && cidade.estado_id != null) {
        const estadoNome = this.getEstadoNome(cidade.estado_id);
        if (estadoNome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('codigoDistritoFaturaEletronica') && cidade.codigoDistritoFaturaEletronica) {
        if (cidade.codigoDistritoFaturaEletronica.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('codigoLocalidadeFaturaEletronica') && cidade.codigoLocalidadeFaturaEletronica) {
        if (cidade.codigoLocalidadeFaturaEletronica.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('criadoEm') && (cidade as any).criadoEm) {
        const dataFormatada = new Date((cidade as any).criadoEm).toLocaleDateString('pt-BR');
        if (dataFormatada.toLowerCase().includes(termLower)) return true;
      }
      return false;
    });

    this.totalRecords = this.cidades.length;
  }

  // =========================================================================
  // Dados
  // =========================================================================

  findEstado(): Promise<void> {
    return new Promise((resolve) => {
      this.estadoService.get('/estado?listarTodos=true').subscribe({
        next: (response) => {
          this.estados = response.content;
          resolve();
        },
        error: () => {
          this.estados = [];
          resolve();
        },
      });
    });
  }

  getEstados(): { label: string; value: number }[] {
    return this.estados.map((estado) => ({
      label: estado.nome,
      value: Number(estado.id),
    }));
  }

  getEstadoNome(estadoId: number): string {
    const estado = this.estados.find(e => e.id === estadoId);
    return estado ? estado.nome : '-';
  }

  findCidade(page: number = 0, size: number = this.rows, sort: string = 'id,asc') {
    this.loading = true;
    this.cidadeService.get('/cidade', { page, size, sort }).subscribe({
      next: (response) => {
        this.cidadesOriginal = response.content || [];
        this.cidades = [...this.cidadesOriginal];
        this.totalRecords = response.totalElements || this.cidadesOriginal.length;
        this.first = page * size;
        this.loading = false;

        if (this.filterName && this.filterName.trim().length > 0) {
          this.filtrarCidadesLocal(this.filterName.trim());
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('Erro_ao_carregar_cidades'),
        });
        this.loading = false;
      },
    });
  }

  // =========================================================================
  // Ordenacao e Paginacao
  // =========================================================================

  onSortChange(event: any): void {
    if (!event.field) return;

    const field = event.field;
    const order = event.order === 1 ? 'asc' : 'desc';

    // Evitar chamada duplicada se mesmo campo e mesma direcao
    if (this.lastfield === field && this.lastSort === order) return;

    this.lastfield = field;
    this.lastSort = order;

    // Ordenar no backend (todos os registros do banco)
    this.first = 0;
    this.findCidade(0, this.rows, `${field},${order}`);
  }

  onPageChange(event: any) {
    const page = event.page;
    const size = event.rows;
    this.rows = event.rows;
    this.findCidade(page, size, `${this.lastfield},${this.lastSort}`);
  }

  onRowsPerPageChange() {
    this.first = 0;
    this.findCidade(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  // =========================================================================
  // CRUD
  // =========================================================================

  submit() {
    this.isFormSubmitted = true;

    if (!this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_nome_cidade_obrigatorio'),
      });
      return;
    }
    if (!this.form.estado) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_estado_obrigatorio'),
      });
      return;
    }

    const body: CidadeDTO = {
      nome: this.form.nome,
      estado_id: this.form.estado.value,
      codigoDistritoFaturaEletronica: this.form.codigoDistritoFaturaEletronica,
      codigoLocalidadeFaturaEletronica: this.form.codigoLocalidadeFaturaEletronica,
      nomeCidadeNotaEletronica: this.form.nomeCidadeNotaEletronica,
      nomeLocalidadeNotaEletronica: this.form.nomeLocalidadeNotaEletronica,
    };

    if (this.form.id) {
      // Edicao (PUT)
      body.id = this.form.id;
      this.cidadeService.put(`/cidade/${this.form.id}`, body).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('Cidade_editada_com_sucesso'),
          });
          this.isFormSubmitted = false;
          this.limparForm();
          this.findCidade();
          this.activeTabIndex = 1;
        },
        error: () => {},
      });
    } else {
      // Criacao (POST)
      this.cidadeService.post('/cidade', body).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('Cidade_salva_com_sucesso'),
          });
          this.isFormSubmitted = false;
          this.limparForm();
          this.findCidade();
          this.activeTabIndex = 1;
        },
        error: () => {},
      });
    }
  }

  deletarCidade(id: number) {
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao() {
    if (this.deleteItemId == null) return;
    this.cidadeService.delete(`/cidade/${this.deleteItemId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Cidade_excluida_com_sucesso'),
        });
        this.findCidade();
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
      error: () => {
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
    });
  }

  createEmptyForm(): CidadeFormDTO {
    return {
      nome: '',
      estado: undefined,
      codigoDistritoFaturaEletronica: '',
      codigoLocalidadeFaturaEletronica: '',
      nomeCidadeNotaEletronica: '',
      nomeLocalidadeNotaEletronica: '',
    };
  }
}
